/**
 * =================================================================
 * LES STATUTS QUI INTERDISENT UNE CLÔTURE — CE QUE L'ADMINISTRATEUR RÈGLE, ET CE QU'IL NE PEUT PAS
 *
 * Décision du commanditaire (18/09/2026) : les statuts bloquants et le message montré au
 * technicien se règlent au dashboard, statut par statut. Deux nuances que l'écran doit
 * rendre visibles, sans quoi il mentirait :
 *
 *   - 90 (facturé) et 99 (clôturé) sont refusés par l'AGENT, en dur, sur la machine du
 *     client. La case est donc cochée ET FIGÉE : la décocher ici ne les ouvrirait pas.
 *     Pour y toucher il faut le kit de provisionnement, donc la machine — c'est le prix
 *     d'un garde-fou que le réseau ne peut pas désarmer ;
 *   - ce que l'administrateur coche ici est appliqué par l'APP (elle refuse la clôture
 *     avant de faire signer) et non par l'agent : c'est une règle métier, pas un
 *     garde-fou de sécurité. `--statuts-verrouilles` du kit, lui, arme l'agent.
 *
 * Module pur, sans React : éprouvé par `backend/__tests__/verrousStatuts.test.js`, qui
 * compare son plancher et ses phrases à ceux de l'AGENT et du SERVEUR. (Le mobile est
 * comparé au serveur par `refusPartage.test.js` : les quatre copies se tiennent par
 * transitivité.)
 * =================================================================
 */

/** Miroir de STATUTS_IRREDUCTIBLES (servup-provisioning/agent-code/lib/missions.js). */
export const PLANCHER_VERROUS: readonly string[] = Object.freeze(['90', '99']);

export const MESSAGE_REFUS_GENERIQUE =
  'Clôture refusée : cet ordre ne peut plus être modifié dans Image.';

const rogner = (v?: string | null): string => (v == null ? '' : String(v).trim());

/** Ce code est-il verrouillé par la machine, quoi que dise le dashboard ? */
export const estVerrouMachine = (code?: string | null): boolean =>
  PLANCHER_VERROUS.includes(rogner(code));

/**
 * La phrase que le technicien lira. MÊME RÈGLE que `backend/utils/messageRefus.js` et
 * `app/sav-app/src/utils/clotureRefus.ts` — les trois sont comparées par un test.
 */
export const messageRefusCloture = (
  entree: { code?: string | null; libelle?: string | null; message?: string | null } = {}
): string => {
  const message = rogner(entree.message);
  if (message) return message;

  const libelle = rogner(entree.libelle);
  if (libelle) return `Clôture refusée : ordre déjà « ${libelle} » dans Image.`;

  const code = rogner(entree.code);
  if (code) return `Clôture refusée : ordre au statut ${code} dans Image.`;

  return MESSAGE_REFUS_GENERIQUE;
};

export interface EtatVerrou {
  /** État de la case à cocher. */
  coche: boolean;
  /** La case est-elle FIGÉE (verrou machine) ? */
  figee: boolean;
  /** Qui porte la règle : la machine, le dashboard, ou personne. */
  porte: 'machine' | 'dashboard' | 'aucun';
  /** L'aperçu exact de ce que le technicien lira. */
  apercu: string;
  /** L'explication à afficher sous la case. */
  aide: string;
}

/**
 * L'état complet d'une ligne de l'écran, pour un code ORETAR.
 *
 * `bloqueSynchro` : un code « Ne pas synchroniser » ne descend jamais dans l'app, donc
 * la question ne se pose pas — la case est éteinte et grisée, comme « Archiver ».
 */
export const etatVerrou = (
  code: string,
  config: { locks_writeback?: boolean | null; locks_writeback_message?: string | null; crm_label?: string | null },
  options?: { ignore?: boolean }
): EtatVerrou => {
  const machine = estVerrouMachine(code);
  const dashboard = config?.locks_writeback === true;
  const apercu = messageRefusCloture({
    code,
    libelle: config?.crm_label,
    message: config?.locks_writeback_message,
  });

  if (machine) {
    return {
      coche: true, figee: true, porte: 'machine', apercu,
      aide: "Toujours bloqué : l'agent refuse d'écrire sur un ordre facturé ou clôturé. "
          + 'Seul le kit de provisionnement, sur la machine, peut le changer.',
    };
  }
  if (options?.ignore) {
    return {
      coche: false, figee: true, porte: 'aucun', apercu,
      aide: 'Sans objet : ce code n’est pas synchronisé, il n’arrive pas dans l’app.',
    };
  }
  return {
    coche: dashboard, figee: false, porte: dashboard ? 'dashboard' : 'aucun', apercu,
    aide: dashboard
      ? "L'app refusera la clôture d'un ordre dans cet état, avant que le technicien fasse signer."
      // On ne promet PAS que la clôture passera : le kit peut avoir armé ce code sur la
      // machine (`--statuts-verrouilles`), et cet écran ne voit pas le `.env` de l'agent.
      : 'Aucun blocage réglé ici (la machine du client peut en ajouter).',
  };
};
